import { ExternalLink, RefreshCw, Trophy } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { usePageTitle } from '../../hooks/usePageTitle';
import { useRequireAuth } from '../../hooks/useRequireAuth';
import { useTRPC } from '../../hooks/useTRPC';
import { QUEST_COMPLETION_API_URL } from '../../lib/config';
import { cn } from '../../lib/utils';
import { captureAndReportError } from '../../utils/errorReporting';
import { useDappDataOptional } from '../../contexts/DappDataContext';
import {
  deriveRoundDateState,
  statusDotToneClass,
  type RoundDateState,
  type StatusTone,
} from '../../pages/quests/questWorkspace';
import { useOptionalDappContext } from '../DappLayout';
import { Button } from '../Button';
import { CopyableValue } from '../common/detailParts';
import {
  PortalCard,
  PortalPage,
  PortalPageHeader,
  Skeleton,
  SkeletonGroup,
} from '../portal';

type QuestTier = 'Easy' | 'Deep';
type QuestStatus = 'Draft' | 'Active' | 'Disabled' | 'Archived';

interface DappQuest {
  id: string;
  questKey: string;
  tier: QuestTier;
  status: QuestStatus;
  title: string;
  description: string;
  completionCriteria: string;
  tips: string[];
  deepLinkUrl: string | null;
  badgeTitle: string;
  badgeImageUrl: string | null;
  sortOrder: number;
  completionCount: number;
}

interface DappQuestRound {
  roundAppId: string;
  roundId: string;
  seasonId: string;
  seasonNumber: number;
  roundNumber: number;
  status: string;
  startsAt: string;
  endsAt: string;
  eligibilityStartsAt: string;
  eligibilityEndsAt: string;
  revealStartsAt: string | null;
  revealEndsAt: string | null;
  appName: string;
  androidPackage: string;
  curationNote: string;
  iconUrl: string | null;
  isUnrevealed: boolean;
  quests: DappQuest[];
}

interface DappQuestsData {
  dappId: string;
  dappName: string;
  androidPackage: string | null;
  rounds: DappQuestRound[];
}

const ROUND_STATE_TONE: Record<RoundDateState, StatusTone> = {
  Unknown: 'neutral',
  Scheduled: 'info',
  Open: 'success',
  Finalizing: 'warn',
  Closed: 'neutral',
  Expired: 'warn',
};

const ROUND_STATE_LABEL: Record<RoundDateState, string> = {
  Unknown: 'Dates TBD',
  Scheduled: 'Upcoming',
  Open: 'Live',
  Finalizing: 'Finalizing',
  Closed: 'Ended',
  Expired: 'Expired',
};

export const DashboardQuests = () => {
  const dappId = useOptionalDappContext()?.dappId;
  const { canRender, isChecking } = useRequireAuth();
  const { dappData } = useDappDataOptional();
  const trpc = useTRPC();
  const [data, setData] = useState<DappQuestsData | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);

  const dappName = data?.dappName || dappData?.dappName || null;

  usePageTitle(
    dappId && dappName
      ? `${dappName} - Quests | Publishing Portal`
      : 'Quests | Publishing Portal',
  );

  useEffect(() => {
    if (!canRender || !dappId) {
      return;
    }

    let cancelled = false;
    setIsLoading(true);
    setError(null);

    trpc.getDappQuests
      .query({ dappId })
      .then((result) => {
        if (cancelled) {
          return;
        }
        if (result._tag === 'Left') {
          setError(result.left.message);
          setData(null);
          return;
        }
        setData(result.right);
      })
      .catch((loadError) => {
        if (cancelled) {
          return;
        }
        captureAndReportError(loadError, {
          component: 'DashboardQuests',
          operation: 'getDappQuests',
          dappId,
        });
        setError('Failed to load quests');
        setData(null);
      })
      .finally(() => {
        if (!cancelled) {
          setIsLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [canRender, dappId, trpc, refreshKey]);

  const rounds = useMemo(() => {
    const sorted = [...(data?.rounds ?? [])];
    sorted.sort(
      (a, b) =>
        a.seasonNumber - b.seasonNumber || a.roundNumber - b.roundNumber,
    );
    return sorted;
  }, [data]);

  if (isChecking || !canRender) {
    return null;
  }

  const isInitialLoad = isLoading && !data;
  const hasContent = Boolean(data && rounds.length > 0);

  return (
    <PortalPage>
      <PortalPageHeader
        icon={<Trophy />}
        title="Quests"
        actions={
          dappId && data ? (
            <Button
              variant="ghostMuted"
              onClick={() => setRefreshKey((key) => key + 1)}
              disabled={isLoading}
              aria-label="Refresh quests"
              title="Refresh"
              className="size-10 px-0"
            >
              <RefreshCw
                className={cn('size-4', isLoading && 'animate-spin')}
              />
            </Button>
          ) : undefined
        }
      />

      <div className="mx-auto w-full max-w-3xl">
        {!dappId ? (
          <EmptyState title="No dApp selected" />
        ) : isInitialLoad ? (
          <QuestsSkeleton />
        ) : error ? (
          <ErrorState
            message={error}
            onRetry={() => setRefreshKey((k) => k + 1)}
          />
        ) : !hasContent || !data ? (
          <EmptyState
            title="No quests yet"
            description="This app isn’t part of any quest rounds right now. Quests appear here once the Solana Mobile team adds it to a challenge season."
          />
        ) : (
          <div className="motion-enter flex flex-col gap-8">
            {rounds.map((round) => (
              <RoundSection key={round.roundAppId} round={round} />
            ))}
            <CompletionApiCard />
          </div>
        )}
      </div>
    </PortalPage>
  );
};

function RoundSection({ round }: { round: DappQuestRound }) {
  const state = deriveRoundDateState(
    {
      startsAt: round.startsAt,
      endsAt: round.endsAt,
      revealStartsAt: round.revealStartsAt ?? '',
      revealEndsAt: round.revealEndsAt ?? '',
    },
    new Date(),
  );

  return (
    <section className="flex flex-col gap-3">
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1 px-1">
        <h2 className="text-base font-semibold text-white">
          Round {round.roundNumber}
        </h2>
        <span className="inline-flex items-center gap-1.5 text-xs text-seeker-400">
          <span
            className={cn(
              'size-1.5 rounded-full',
              statusDotToneClass[ROUND_STATE_TONE[state]],
            )}
            aria-hidden="true"
          />
          {ROUND_STATE_LABEL[state]}
        </span>
        <span className="ml-auto text-xs tabular-nums text-seeker-500">
          Season {round.seasonNumber} ·{' '}
          {formatDateRange(round.startsAt, round.endsAt)}
        </span>
      </div>

      <PortalCard>
        <div className="divide-y divide-seeker-800">
          {round.quests.map((quest) => (
            <QuestRow key={quest.id} quest={quest} />
          ))}
        </div>
      </PortalCard>
    </section>
  );
}

function QuestRow({ quest }: { quest: DappQuest }) {
  return (
    <div className="flex gap-4 p-5">
      <QuestBadgeArt
        imageUrl={quest.badgeImageUrl}
        title={quest.badgeTitle}
        tier={quest.tier}
      />

      <div className="min-w-0 flex-1">
        <div className="flex items-baseline justify-between gap-3">
          <h3
            className="truncate text-sm font-medium text-white"
            title={quest.title}
          >
            {quest.title}
          </h3>
          <span className="shrink-0 text-xs text-seeker-500">
            {quest.tier}
            {quest.status !== 'Active' ? ` · ${quest.status}` : ''}
          </span>
        </div>

        {quest.description && (
          <p className="mt-1 text-sm leading-5 text-seeker-400">
            {quest.description}
          </p>
        )}

        <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-1.5 text-xs text-seeker-500">
          <CopyableValue
            value={quest.questKey}
            display={
              <span className="font-mono text-xs text-seeker-400">
                {quest.questKey}
              </span>
            }
            truncate={false}
          />
          <span className="tabular-nums">
            {quest.completionCount.toLocaleString()}{' '}
            {quest.completionCount === 1 ? 'completion' : 'completions'}
          </span>
          {quest.deepLinkUrl && (
            <a
              href={quest.deepLinkUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1 rounded text-seeker-400 transition-colors duration-150 hover:text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/70"
            >
              <ExternalLink className="size-3.5" />
              Open
            </a>
          )}
        </div>
      </div>
    </div>
  );
}

function QuestBadgeArt({
  imageUrl,
  title,
  tier,
}: {
  imageUrl: string | null;
  title: string;
  tier: QuestTier;
}) {
  if (imageUrl) {
    return (
      <img
        src={imageUrl}
        alt=""
        aria-hidden="true"
        // Painted seeker-800 until the bitmap lands, then fades it in (no pop-in).
        onLoad={(event) => event.currentTarget.classList.remove('opacity-0')}
        className="size-10 shrink-0 rounded-lg bg-seeker-800 object-cover opacity-0 ring-1 ring-inset ring-seeker-700 transition-opacity duration-200 ease-out"
      />
    );
  }

  return (
    <div
      className={cn(
        'flex size-10 shrink-0 items-center justify-center rounded-lg',
        tier === 'Deep' ? 'bg-blue-900/30' : 'bg-seeker-800',
      )}
      aria-hidden="true"
      title={title}
    >
      <Trophy
        className={cn(
          'size-5',
          tier === 'Deep' ? 'text-blue-400' : 'text-seeker-400',
        )}
      />
    </div>
  );
}

function CompletionApiCard() {
  return (
    <PortalCard className="p-5">
      <div className="flex items-center gap-2 text-sm font-medium text-white">
        <ExternalLink className="size-4" />
        Reporting completions
      </div>
      <p className="mt-1.5 text-xs leading-4 text-seeker-400">
        From your backend, POST to this endpoint with your{' '}
        <code className="font-mono text-seeker-300">X-API-Key</code> and a
        quest’s <code className="font-mono text-seeker-300">quest_id</code>{' '}
        (shown above).
      </p>

      <div className="mt-3 flex items-center gap-2 rounded-lg bg-seeker-800 px-3 py-2">
        <span className="shrink-0 font-mono text-xs font-semibold text-green-400">
          POST
        </span>
        <CopyableValue
          value={QUEST_COMPLETION_API_URL}
          display={
            <span className="font-mono text-xs text-seeker-300">
              {QUEST_COMPLETION_API_URL}
            </span>
          }
          truncate
          className="min-w-0 flex-1"
        />
      </div>
    </PortalCard>
  );
}

function EmptyState({
  title,
  description,
}: {
  title: string;
  description?: string;
}) {
  return (
    <PortalCard className="flex flex-col items-center px-6 py-16 text-center">
      <div className="flex size-12 items-center justify-center rounded-full bg-seeker-800">
        <Trophy className="size-5 text-seeker-500" />
      </div>
      <div className="mt-4 text-sm font-medium text-white">{title}</div>
      {description && (
        <div className="mx-auto mt-1.5 max-w-sm text-xs leading-4 text-seeker-400">
          {description}
        </div>
      )}
    </PortalCard>
  );
}

function ErrorState({
  message,
  onRetry,
}: {
  message: string;
  onRetry: () => void;
}) {
  return (
    <div className="rounded-lg border border-rose-500/40 bg-rose-900/20 p-5">
      <div className="text-sm font-medium text-rose-400">{message}</div>
      <Button variant="outlineMuted" onClick={onRetry} className="mt-4">
        Retry
      </Button>
    </div>
  );
}

/**
 * Mirrors one `RoundSection`: the 24px round header row and a card with two
 * `QuestRow`s (40px badge, 20px title, 20px description, 16px meta row).
 */
function QuestsSkeleton() {
  return (
    <SkeletonGroup label="Loading quests" className="flex flex-col gap-8">
      <div className="flex flex-col gap-3">
        <div className="flex h-6 items-center justify-between gap-3 px-1">
          <Skeleton className="h-4 w-24" />
          <Skeleton className="h-3 w-44" />
        </div>
        <PortalCard>
          <div className="divide-y divide-seeker-800">
            {[0, 1].map((index) => (
              <div key={index} className="flex gap-4 p-5">
                <Skeleton className="size-10 shrink-0" rounded="lg" />
                <div className="min-w-0 flex-1">
                  <div className="flex h-5 items-center justify-between gap-3">
                    <Skeleton className="h-3.5 w-44" />
                    <Skeleton className="h-3 w-10" />
                  </div>
                  <div className="mt-1 flex h-5 items-center">
                    <Skeleton className="h-3.5 w-full" />
                  </div>
                  <div className="mt-3 flex h-4 items-center gap-4">
                    <Skeleton className="h-3 w-28" />
                    <Skeleton className="h-3 w-20" />
                  </div>
                </div>
              </div>
            ))}
          </div>
        </PortalCard>
      </div>
    </SkeletonGroup>
  );
}

const dateRangeFormatter = new Intl.DateTimeFormat(undefined, {
  day: 'numeric',
  month: 'short',
  year: 'numeric',
});

function formatDateRange(start: string, end: string): string {
  const startDate = new Date(start);
  const endDate = new Date(end);
  const startValid = !Number.isNaN(startDate.getTime());
  const endValid = !Number.isNaN(endDate.getTime());

  if (!startValid && !endValid) {
    return 'Dates TBD';
  }
  if (startValid && endValid) {
    return `${dateRangeFormatter.format(startDate)} – ${dateRangeFormatter.format(endDate)}`;
  }
  return dateRangeFormatter.format(startValid ? startDate : endDate);
}
